import React from 'react';
import { Card, Stack, Button } from 'react-bootstrap';
import { lowerCase } from 'lodash';
import { useTranslation } from 'react-i18next';
import { map } from 'lodash';
import { Link } from 'react-router-dom';

import { amountToUsd, amountToKhr } from '@utils/currency';
import { InsurancePolicy } from '@models/InsurancePolicy';
import { InsurerLogoWrapper } from '@components/Feature/Insurer';
import { AmountWithDiscount } from '@components/Base';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil } from '@fortawesome/free-solid-svg-icons';
import { MGA_ADMIN_URL } from '@config';

type Props = Pick<InsurancePolicy,
  | 'insurer'
  | 'adminFeeFinalAmount'
  | 'finalPremium'
  | 'totalPremium'
  | 'totalPremiumKhr'
  | 'policyBenefitTemplates'> & {
    hidePremium?: boolean;
    canEdit?: boolean;
  };

const InsurancePolicyPremiumCardBody = ({
  insurer,
  policyBenefitTemplates,
  adminFeeFinalAmount,
  finalPremium,
  totalPremium,
  totalPremiumKhr,
  hidePremium = false,
  canEdit = false,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Card.Body>
      <InsurerLogoWrapper insurer={insurer} />

      <div className="mb-2 pb-2 border-bottom border-bottom-dashed">
        <div className="d-flex justify-content-between">
          <span className="fw-bold">{t('common:policyTerm')}:</span>
          <span>{`1 ${t('common:year')}`}</span>
        </div>
        <div className="d-flex justify-content-between">
          <span className="fw-bold">{t('common:premiumPaymentTerm')}:</span>
          <span>1 {t('common:year')}</span>
        </div>
      </div>

      {policyBenefitTemplates && (
        <div className="mb-2 pb-2 border-bottom border-bottom-dashed">
          <div className="d-flex">
            <span className="fw-bold">{t('common:planType')}:</span>
          </div>
          {map(policyBenefitTemplates, ({ id, name }) => (
            <Stack key={name} direction="horizontal" gap={2}>
              {canEdit && (
                <Button
                  href={`${MGA_ADMIN_URL}/policy_benefit_templates/${id}`}
                  variant="outline-info"
                  size="sm"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <FontAwesomeIcon icon={faPencil} />
                </Button>
              )}
              <div>{name}</div>
            </Stack>
          ))}
        </div>
      )}

      {!hidePremium && (
        <>
          <div className="mb-2 pb-2 border-bottom border-bottom-dashed">
            <div className="d-flex justify-content-between">
              <span className="fw-bold">{t('common:premium')}:</span>
              <AmountWithDiscount finalAmount={finalPremium} />
            </div>

            <div className="d-flex justify-content-between">
              <span className="fw-bold">{t('common:adminFee')}:</span>
              <AmountWithDiscount finalAmount={adminFeeFinalAmount} />
            </div>
          </div>

          <div className="list-unstyled pb-2 mb-0">
            <div className="d-flex justify-content-between">
              <span className="fw-bold">{t('common:youWillPay')}:</span>
              <div>
                <div>{amountToUsd(totalPremium)}/{lowerCase(t('common:year'))}</div>
                <div>{amountToKhr(totalPremiumKhr)}/{lowerCase(t('common:year'))}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </Card.Body>
  );
};

export { InsurancePolicyPremiumCardBody };
